import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { notify } from 'components/Common/Toast';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { updateTableState } from 'redux/slices/common/appSlice';
import { RootState } from 'redux/store';
import * as announcementsApi from 'services/announcementsApi';
import { Announcement, AnnouncementState } from 'types/announcements';
import { sortByField } from 'utils/helpers';

const initialState: AnnouncementState = {
    data: [],
    loading: false,
    error: false,
    totalCount: 0,
    inflightParishId: null,
};

export const deleteAnnouncementById = createAsyncThunkWrapper<{ ok: true; parishId: string } | null, { parishId: string; id: string }>(
    'announcements/deleteAnnouncementById',
    async ({ parishId, id }, { dispatch, getState }) => {
        if (!parishId || !id) return null;
        try {
            await announcementsApi.deleteAnnouncement(parishId, id);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Announcement deleted successfully', 'success');
            // Deleting the only/last announcement on the current (server-fetched) page would
            // otherwise re-request that same now-out-of-range page below. Reset first — the
            // refetch's thunk reads pageNumber/pageSize fresh via getState(), so this dispatch
            // (synchronous) is already reflected by the time it runs, with no extra request.
            const { pageNumber = 0, pageSize = 20 } = (getState() as RootState).app.table;
            if (pageNumber !== 0) {
                dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
            }
            dispatch(fetchAnnouncements({ parishId }));
            return { ok: true as const, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to delete announcement' }
);

export const fetchAnnouncements = createAsyncThunkWrapper<
    { items: Announcement[]; total: number; parishId: string } | null,
    { parishId: string }
>(
    'announcements/fetchAnnouncements',
    async ({ parishId }, { getState }) => {
        if (!parishId) return null;
        const state = getState() as RootState;
        const { pageNumber = 0, pageSize = 20 } = state.app.table || {};
        try {
            const res = await announcementsApi.listAnnouncements(parishId, {
                page: (pageNumber || 0) + 1,
                pageSize: pageSize || 20,
            });
            if (parishSelectionMoved(getState, parishId)) return null;
            return { items: res.items, total: res.total, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load announcements' }
);

const announcementsSlice = createSlice({
    name: 'announcements',
    initialState,
    reducers: {
        clearAnnouncements(state) {
            state.data = [];
            state.totalCount = 0;
            state.inflightParishId = null;
            state.error = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAnnouncements.pending, (state, action) => {
            state.loading = true;
            state.error = false;
            state.data = [];
            state.inflightParishId = action.meta.arg.parishId;
        });
        builder.addCase(fetchAnnouncements.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.inflightParishId === action.meta.arg.parishId) {
                    state.loading = false;
                }
                return;
            }
            const { items, total, parishId } = action.payload;
            if (parishId !== state.inflightParishId) return;
            state.loading = false;
            state.data = sortByField(items, 'createDate', 'DESC') || [];
            state.totalCount = total;
        });
        builder.addCase(fetchAnnouncements.rejected, (state, action) => {
            if (state.inflightParishId === action.meta.arg.parishId) {
                state.loading = false;
                state.error = true;
            }
        });
    },
});

export const { clearAnnouncements } = announcementsSlice.actions;
export default announcementsSlice.reducer;
